import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { K8sNamespaceInfo } from '@/types/dashboard/kubernetes/K8sNamespace'
import { useAxios } from '@/composables/useAxios'
import { STATUS_CODES } from '@/constants'
import { z } from 'zod'

export const useK8sStore = defineStore('k8s', () => {
  const namespaces = ref<z.infer<typeof K8sNamespaceInfo>[]>([])
  const activeConfigId = ref<number | null>(null)
  const loaded = ref(false)

  const hasMultipleNamespaces = computed(() => namespaces.value.length > 1)
  const hasNamespaces = computed(() => namespaces.value.length > 0)
  const activeConfig = computed(() =>
    namespaces.value.find((n) => n.id === activeConfigId.value) ?? null
  )

  async function fetchNamespaces() {
    try {
      const res = await useAxios<null, z.infer<typeof K8sNamespaceInfo>[]>(
        'GET',
        '/k8s/namespaces',
        true
      )
      if (res?.status === STATUS_CODES.OK && res.data) {
        const parsed = z.array(K8sNamespaceInfo).safeParse(res.data)
        if (parsed.success) {
          namespaces.value = parsed.data
          // Auto-select first active namespace if none selected
          if (activeConfigId.value === null && parsed.data.length > 0) {
            const active = parsed.data.find((n) => n.status === 'active')
            const first = parsed.data[0]
            activeConfigId.value = active?.id ?? first?.id ?? null
          }
        }
      }
    } finally {
      loaded.value = true
    }
  }

  function setActive(id: number) {
    activeConfigId.value = id
  }

  function $reset() {
    namespaces.value = []
    activeConfigId.value = null
    loaded.value = false
  }

  return {
    namespaces,
    activeConfigId,
    loaded,
    hasMultipleNamespaces,
    hasNamespaces,
    activeConfig,
    fetchNamespaces,
    setActive,
    $reset
  }
})
